import { Box } from '@mui/material'
import * as React from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { A11y, Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Swiper as SwiperType } from 'swiper/types'

import 'swiper/css'
import 'swiper/css/navigation'

import { debounce } from '@/shared/lib'
import { Arrow } from '@/shared/ui/Arrow'
import { VodArrowLeft, VodArrowRight } from '@/shared/ui/Icons'
import {
    vodSliderButtonHeight,
    vodSliderButtonWidth,
} from '@/shared/constants/vodConstants'
import VodItem from '@/features/VOD/vodClasses/vodItem'
import { pageType } from '@/shared/store/types'
import VodItemView from '@/features/VOD/components/vodItem/VodItemView'
import { useVodItemsContext } from '@/features/VOD/context/UseGetVodItemContext/useVodItemsContext'
import VodContentSkeleton from '@/features/VOD/components/skeletons/VodContentSkeleton'

interface ISlider {
    buttonHeight?: number

    vodItemHeight: number
    handleGoToCategoryButtonClick: (
        itemId: string,
        title: string,
        type: pageType
    ) => void
    scrollPosition: {
        x: number
        y: number
    }
    hideCaptions?: boolean
    hideProgress?: boolean
    rowHeightPercentage?: number
    cardRadiusDp?: number
    isHomeContentRow?: boolean
}

const VodSlider = ({
    buttonHeight,
    vodItemHeight,
    handleGoToCategoryButtonClick,
    scrollPosition,
    hideCaptions,
    hideProgress,
    rowHeightPercentage,
    cardRadiusDp,
    isHomeContentRow,
}: ISlider) => {
    const swiperRef = useRef<SwiperType | null>(null)
    const swipe = swiperRef.current
    const [isAtStart, setIsAtStart] = useState(true)
    const [isAtEnd, setIsAtEnd] = useState(false)
    const { rowItems, getItems } = useVodItemsContext()

    const debouncedGetItems = useMemo(
        () =>
            debounce(() => {
                getItems()
            }, 300),
        []
    )
    const handleSlideChange = () => {
        if (swipe) {
            setIsAtStart(swipe.isBeginning)
            setIsAtEnd(swipe.isEnd)
        }
    }

    const leftClick = useCallback(() => {
        swipe?.slidePrev()
        handleSlideChange()
    }, [swipe, handleSlideChange])

    const rightClick = () => {
        swipe?.slideNext()
        handleSlideChange()
        if (swipe.isEnd) {
            debouncedGetItems()
        }
    }

    const LeftArrowWrapper = {
        position: 'absolute',
        left: 0,
        top: 0,
        width: {
            xs: '15%',
            md: buttonHeight ? buttonHeight * 0.4 : vodSliderButtonWidth,
        },
        height: buttonHeight ? buttonHeight : vodSliderButtonHeight,
        opacity: isAtStart ? 0 : 1,
        pointerEvents: 'all',
        cursor: 'pointer',
    }

    const RightArrowWrapper = {
        position: 'absolute',
        right: 0,
        top: 0,
        width: {
            xs: '15%',
            md: buttonHeight ? buttonHeight * 0.4 : vodSliderButtonWidth,
        },
        height: buttonHeight ? buttonHeight : vodSliderButtonHeight,
        opacity: isAtEnd ? 0 : 1,
        pointerEvents: 'all',
        cursor: 'pointer',
    }

    const shadowBoxStyle = {
        position: 'absolute',
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        background: 'black',
        opacity: 0.3,
        top: 0,
        left: 0,
        zIndex: 8,
        pointerEvents: 'all',
        cursor: 'pointer',
    }

    useEffect(() => {
        if (!swipe) return
        swipe.on('slideChange', handleSlideChange)
        setIsAtStart(swipe.isBeginning)
        setIsAtEnd(swipe.isEnd)

        return () => {
            swipe.off('slideChange', handleSlideChange)
        }
    }, [rowItems])

    useEffect(() => {
        if (!swipe) return
        setIsAtStart(swipe?.isBeginning)
        setIsAtEnd(swipe?.isEnd)
    })

    if (!rowItems.length) return <VodContentSkeleton rows={1} items={6} />

    return (
        <Box position="relative">
            <Swiper
                onBeforeInit={(swiper: SwiperType) =>
                    (swiperRef.current = swiper)
                }
                modules={[Navigation, Pagination, A11y]}
                spaceBetween={16}
                slidesPerGroup={2}
                slidesPerView="auto">
                {rowItems.map((item: VodItem, index) => (
                    <SwiperSlide key={index} style={{ width: 'min-content' }}>
                        <VodItemView
                            hideCaptions={hideCaptions}
                            hideProgress={hideProgress}
                            vodItemHeight={vodItemHeight}
                            cardRadiusDp={cardRadiusDp}
                            rowHeightPercentage={rowHeightPercentage}
                            item={item}
                            handleGoToCategoryButtonClick={
                                handleGoToCategoryButtonClick
                            }
                            scrollPosition={scrollPosition}
                            isHomeContentItem={isHomeContentRow}
                        />
                    </SwiperSlide>
                ))}
            </Swiper>

            <Box position="absolute" top="30%" left="0px" width="100%">
                <Box
                    sx={LeftArrowWrapper}
                    onClick={(event) => {
                        event.stopPropagation()
                        leftClick()
                    }}>
                    <Arrow icon={<VodArrowLeft />} />
                    <Box sx={shadowBoxStyle} />
                </Box>

                <Box
                    sx={RightArrowWrapper}
                    onClick={(event) => {
                        event.stopPropagation()
                        rightClick()
                    }}>
                    <Arrow icon={<VodArrowRight />} />
                    <Box sx={shadowBoxStyle} />
                </Box>
            </Box>
        </Box>
    )
}

export default VodSlider
